import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box } from "rizzui/box";
import { Text } from "rizzui/typography";
import { useDropzone } from "react-dropzone";
import CSVIcon from "../../assets/csv";
import cn from "../../lib/utils/class-name";
import { Progressbar } from "rizzui/progressbar";
import BinIcon from "../../assets/bin";
import { ActionIcon } from "rizzui/action-icon";
import { Button } from "rizzui/button";
import { useModal } from "../../hooks/useModal";
import PDFIcon from "../../assets/pdf";
import Select from "react-select";
import { labelClassName } from "../../modules/projects/new/first";
import { useNavigate } from "react-router";
import axios from "axios";
import { useCampaigns } from "../../hooks/useCampaign";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { baseUrl } from "../../modules/account/login";
import { ThemeContext } from "../../context/themeContext";
import { AuthContext } from "../../context/authContext";
import { useColorStyle } from "../../hooks/useColorStyle";
import { useNotifcication } from "../../hooks/useNotification";
import ReviewContact from "../reviewContacts";
import { useLocation } from "react-router";
import SingleEntry from "../singleEntry";

export const textStyle =
  "font-switzer font-[400] text-[14px] text-[#758599] leading-[18.48px]";
export const spanStyle = "font-[500] text-primary-dark underline";
export const buttonTextStyle =
  " w-[92px] h-[35px] rounded-[4px] text-[12px] font-gilroyMedium font-[400] leading-[14.56px]";

export default function ImportFile({
  page = "leads",
  type = "csv",
  id,
  shouldShowSingleEntry = false,
}: {
  page: string;
  type: string;
  id?: string;
  shouldShowSingleEntry?: boolean;
}) {
  const { closeModal, view } = useModal();
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [fileName, setFileName] = useState("");
  const [addedFile, setAddedFile] = useState<File>();
  const [progress, setProgress] = useState(0);
  const [campaign, setCampaign] = useState<any>();

  const location = useLocation();

  const onDrop = useCallback(
    (acceptedFiles) => {
      setError("");
      const ext = acceptedFiles[0].name.split(".").pop().toLowerCase();
      if (acceptedFiles.length > 1) {
        setError("You can only upload one file at a time");
      } else if (ext !== "csv") setError("Upload a CSV file");
      else {
        setIsLoading(true);
        setFileName(acceptedFiles[0].name);
        setAddedFile(acceptedFiles[0]);
        const reader = new FileReader();
        reader.readAsDataURL(acceptedFiles[0]);
        reader.onprogress = (e) => {
          setProgress(Math.ceil((e.loaded / e.total) * 100));
        };
        reader.onload = (e) => {
          setIsDone(true);
        };
      }
    },
    [addedFile]
  );

  const handleDelete = () => {
    setIsLoading(false);
    setIsDone(false);
    setFileName("");
    setAddedFile(undefined);
    setError("");
  };

  const hanldeImport = () => {
    if (addedFile && campaign?.value) uploadCSV();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
  });
  const navigate = useNavigate();
  const { data, isLoading: campaignLoading } = useCampaigns();
  const queryClient = useQueryClient();
  const campaigns = data?.map((campaign) => ({
    value: campaign._id,
    label: campaign.campaign_name,
  }));

  useEffect(() => {
    if (id && campaigns?.length) {
      const selectedCampaign = campaigns.find(
        (campaign: { value: string; label: string }) => campaign.value === id
      );
      if (selectedCampaign && selectedCampaign.value !== campaign?.value) {
        setCampaign(selectedCampaign);
      }
    }
  }, [id, campaigns]);

  const { openNotification } = useNotifcication();
  const { openModal } = useModal();
  const { theme } = useContext(ThemeContext);
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token);
  const { colorStyles } = useColorStyle(theme);
  const handleCSVEntry = () => {
    const formData = new FormData();
    formData.append("leads_csv", addedFile!);
    formData.append("campaign_id", campaign?.value);
    return axios
      .post(`${baseUrl}/leads/upload_leads`, formData, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        if (response?.data?.success) {
          queryClient.invalidateQueries({
            predicate: (query) => query.queryKey[0] === "leads",
          });
          queryClient.invalidateQueries({
            queryKey: [campaign?.value, "campaign_analytics", token],
          });
          openNotification({
            status: "success",
            message: "Leads Added",
          });
          setIsLoading(false);
          setIsDone(false);
          setFileName("");
          closeModal();
          setCampaign("");
          setAddedFile(undefined);
          location?.pathname === "/campaigns/new" &&
            setTimeout(() => navigate("/campaigns"), 1000);
        }
        return response;
      })
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired")
          navigate("/account/logout");
        else if (
          e?.response?.data?.details?.message ===
          "Duplicate records found in the file"
        )
          openModal({
            view: (
              <ReviewContact
                newCampaign={campaign?.value}
                leads={e?.response?.data?.details?.details?.totalRows}
                overrideKey={e?.response?.data?.details?.details?.override_key}
                leads_csv={addedFile}
              />
            ),
          });
        return Promise.reject(
          new Error(e?.response?.data?.message ?? "Something went wrong")
        );
      });
  };

  const {
    mutate: uploadCSV,
    reset: entryReset,
    error: entryError,
    isPending: entryLoading,
  } = useMutation({
    mutationFn: handleCSVEntry,
    retry: 0,
  });

  useEffect(() => {
    entryReset();
    setIsLoading(false);
    setIsDone(false);
    setFileName("");
    setAddedFile(undefined);
    setError("");
  }, [view]);

  return (
    <Box className="sm:w-[471px] w-[96%] mx-auto min-h-[274px] dark:bg-[#2A2C30] bg-background border-[#758599] border-[.5px] rounded-[8px] p-[32px] flex flex-col gap-[32px] text-primary-dark shadow-md">
      <button
        className="absolute top-4 right-4 p-1 rounded-lg z-10 text-gray-400 bg-transparent hover:text-gray-600 dark:hover:text-gray-300"
        onClick={closeModal}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="size-[18px]"
        >
          <path
            fillRule="evenodd"
            d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      <div className="w-[315px]">
        <Text className="font-ibmPlexSerif font-[600] text-[18px] leading-[23.4px] mb-[16px]">
          Import a file
        </Text>
        <Text className={textStyle}>
          Upload a {type} to import your contacts. <br /> The file should follow
          this format,{" "}
          <span className={spanStyle}>
            <a href="/RC_ sample_csv_contacts.csv" download>
              click here
            </a>
          </span>
        </Text>
      </div>

      <div>
        <div className="justify-between flex mb-1">
          <Text className={labelClassName}>Campaign name</Text>
          <span
            onClick={() => navigate("/campaigns/new")}
            className="cursor-pointer"
          >
            {!isLoading && (
              <Text className="underline font-switzer font-[500] text-[12px] leading-[15.84px] text-primary-dark">
                Create new campaign
              </Text>
            )}
          </span>
        </div>
        <Select
          options={campaigns}
          placeholder=""
          styles={colorStyles}
          isDisabled={id ? true : false}
          isSearchable={true}
          value={campaign}
          onChange={(newValue) => {
            setCampaign(newValue);
          }}
          isLoading={campaignLoading}
        />
      </div>

      <div
        className={cn(
          "border border-[#758599] border-dashed rounded-[8px] flex place-content-center place-items-center justify-center",
          isLoading ? "h-[79px] py-[16px]" : "",
          isDone ? "h-[52px] py-[16px]" : "",
          !isDone && !isLoading ? "h-[102px]" : "",
          isDragActive ? "dark:bg-[#FFFFFF]/10 bg-[#000]/10" : "",
          error ? "border-red" : ""
        )}
      >
        {!isLoading ? (
          <div
            {...getRootProps({
              onClick: () => {
                setError("");
              },
            })}
            className="min-w-[172px] place-content-center place-items-center text-center"
          >
            <div className="flex justify-center">
              {page === "leads" && <CSVIcon className={"text-center"} />}
              {page === "projects" && <PDFIcon className={"text-center"} />}
            </div>
            <Text className={cn(textStyle, "mt-[10px]")}>
              Drag and drop a {type} here <br />
              or click{" "}
              <span className={cn(spanStyle, "cursor-pointer")}>Browse</span> to
              select a {type} file
            </Text>
            <input
              {...getInputProps()}
              type="file"
              multiple={false}
              accept=".csv"
            />
          </div>
        ) : (
          <div className="grid grid-cols-8 gap-[4px] w-[90%]">
            <div className="col-span-1 place-content-center">
              {page === "leads" && (
                <CSVIcon
                  className={`${
                    isDone ? "size-[24px]" : "size-[39px]"
                  } inline-block`}
                />
              )}

              {page === "projects" && (
                <PDFIcon
                  className={`${
                    isDone ? "size-[24px]" : "size-[39px]"
                  } inline-block`}
                />
              )}
            </div>
            <div className="col-span-7 items-center flex ">
              <div className="w-full grid grid-cols-1 place-content-center">
                <Text
                  className={cn(
                    textStyle,
                    "text-[12px] w-full leading-[16px] truncate",
                    isDone ? "mb-0" : "mb-[12px]"
                  )}
                  title={fileName}
                >
                  {fileName}
                </Text>
                {!isDone && (
                  <Progressbar
                    size="sm"
                    value={progress}
                    trackClassName="dark:bg-btn"
                  />
                )}
              </div>

              <div className="place-items-end">
                <ActionIcon
                  variant="text"
                  onClick={handleDelete}
                  className="size-[17px] !p-[0px]"
                >
                  <BinIcon className="inline-block" />
                </ActionIcon>

                {!isDone && (
                  <Text className={cn(textStyle, "text-[12px]")}>
                    {progress}%
                  </Text>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {error && (
        <Text
          className={cn(
            textStyle,
            "text-red !leading-[0px] -mt-[16px] text-center"
          )}
        >
          {error}
        </Text>
      )}
      {entryError && (
        <Text className="text-red font-switzer text-[12px] -mt-[24px] font-[400]">
          {entryError.message}
        </Text>
      )}
      {isDone && (
        <div className="flex justify-end gap-[20px]">
          <Button
            variant="text"
            className={cn("bg-muted", buttonTextStyle)}
            onClick={() => {
              handleDelete();
              setCampaign("");
              closeModal();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="text"
            isLoading={entryLoading}
            disabled={addedFile && campaign?.value ? false : true}
            className={cn(
              "bg-black dark:bg-btn text-background hover:text-background",
              buttonTextStyle
            )}
            onClick={() => {
              hanldeImport();
            }}
          >
            Import
          </Button>
        </div>
      )}
      {shouldShowSingleEntry && (
        <Text className="font-switzer font-[400] text-[14px] leading-[15.84px] text-[#758599] text-center">
          Want to enter a single contact?{" "}
          <span
            className="text-primary-dark underline font-[500] cursor-pointer"
            onClick={() => {
              openModal({
                view: <SingleEntry id={id} />,
              });
            }}
          >
            Click here
          </span>
        </Text>
      )}
    </Box>
  );
}
